<template>
    <div v-if="data" style="margin: 80px; font-family: Arial, sans-serif">
        <VJoyText class="joy-mb-4">
            {{ $t('compliance.affidavit.declarant') }}:
            <strong>{{ data.firstName }} {{ data.lastName }}</strong>
        </VJoyText>
        <VJoyText class="joy-mb-4">
            {{ $t('compliance.affidavit.company') }}:
            <strong>{{ data.companyName }}</strong>
        </VJoyText>
        <VJoyText class="joy-mb-4">
            {{ $t('compliance.affidavit.registration.number') }}:
            <strong>{{ data.registrationNumber }}</strong>
        </VJoyText>
        <VJoyText>
            {{ $t('compliance.affidavit.address') }}:
            <strong>{{ data.formattedAddress }}</strong>
        </VJoyText>

        <template v-if="affidavitType === AffidavitEligibilityTemplate.FrYoungCompany">
            <VJoyText class="joy-mt-8 joy-mb-4">
                {{
                    $t('compliance.affidavit.company.age.declaration', {
                        0: `${data.firstName} ${data.lastName}`,
                    })
                }}
            </VJoyText>
            <VJoyText class="joy-mb-4">{{ $t('compliance.affidavit.company.age.statement') }}</VJoyText>
            <VJoyText>{{ $t('compliance.affidavit.honor.statement') }}</VJoyText>
        </template>

        <template v-else-if="affidavitType === AffidavitEligibilityTemplate.FrNoEmployee">
            <VJoyText class="joy-mt-8 joy-mb-4">
                {{
                    $t('compliance.affidavit.no.remuneration.declaration', {
                        0: `${data.firstName} ${data.lastName}`,
                    })
                }}
            </VJoyText>
            <VJoyText class="joy-mb-4">{{ $t('compliance.affidavit.no.remuneration.statement') }}</VJoyText>
            <VJoyText>{{ $t('compliance.affidavit.honor.statement') }}</VJoyText>
        </template>

        <template v-else-if="affidavitType === AffidavitEligibilityTemplate.EsActividadNoHabitual">
            <VJoyText class="joy-mt-8">
                {{
                    $t('compliance.affidavit.es.declaration', {
                        0: `${data.firstName} ${data.lastName}`,
                    })
                }}
            </VJoyText>
        </template>

        <template v-else-if="affidavitType === AffidavitEligibilityTemplate.ChNoBusinessYet">
            <VJoyText class="joy-mt-8 joy-mb-4">
                {{
                    $t('compliance.affidavit.ch.declaration', {
                        0: `${data.firstName} ${data.lastName}`,
                    })
                }}
            </VJoyText>
            <VJoyText class="joy-mb-4">{{ $t('compliance.affidavit.ch.statement') }}</VJoyText>
            <VJoyText class="joy-mb-4">{{ $t('compliance.affidavit.ch.formal.commitment') }}</VJoyText>
            <ul class="joy-mb-4">
                <li>{{ $t('compliance.affidavit.ch.commitment.1') }}</li>
                <li>{{ $t('compliance.affidavit.ch.commitment.2') }}</li>
            </ul>
            <VJoyText>{{ $t('compliance.affidavit.honor.statement') }}</VJoyText>
        </template>

        <VJoyText class="joy-mt-8">
            {{ $t('compliance.affidavit.certified.by') }}:
            <strong class="joy-font-size-primary-600" style="font-family: cursive">{{ data.firstName }} {{ data.lastName }}</strong>
            <br />
            {{ $t('compliance.affidavit.date') }}:
            <strong>{{ formatDate(certificationDate) }}</strong>
        </VJoyText>
    </div>
</template>

<script setup lang="ts">
    import {useFormat, useLocale, useRoute} from '#imports';
    import {VJoyText} from '@maltjoy/core-vue';
    import {AffidavitEligibilityTemplate} from '@api';
    import {useAffidavitStore} from '~/stores/affidavit.store';

    function isAffidavitEligibilityTemplate(value: unknown): value is AffidavitEligibilityTemplate {
        return (Object.values(AffidavitEligibilityTemplate) as string[]).includes(value as string);
    }

    const {formatDate} = useFormat();

    const route = useRoute();
    const affidavitId = route.params.affidavitId as string;
    const localeParam = route.query.locale as string | undefined;
    const affidavitTypeParam = route.query.affidavitType;
    if (!isAffidavitEligibilityTemplate(affidavitTypeParam)) {
        throw new Error('affidavitType query parameter is required and must be a valid AffidavitEligibilityTemplate');
    }
    const affidavitType = affidavitTypeParam;

    if (localeParam) {
        const {updateLocale} = useLocale();
        updateLocale(localeParam);
    }

    const affidavitStore = useAffidavitStore();
    const data = await affidavitStore.fetchAffidavitPdfData(affidavitId);

    const certificationDate = data.submittedAt;
</script>
